import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";
import PrepararProyecto from "./PrepararProyecto";
import ComentariosHilo from "./comentarios/ComentariosHilo";
import ComentariosProyectoAdmin from "./comentarios/ComentariosProyectoAdmin";
import AccesosClientes from "./accesos/AccesosClientes";
import {
  resumenComentarios, agruparResumenPorProyecto, suscribirComentarios, HILO_GENERAL,
} from "../lib/comentarios";
import { progresoTareas } from "../lib/progreso";
import { fmtRelativo } from "../lib/formato";
import {
  Plus, ChevronLeft, RefreshCw, Loader, AlertCircle,
  Check, FolderKanban, Calendar, User, ChevronDown, ChevronRight,
  Circle, CheckCircle2, MoreHorizontal, ExternalLink, Clock,
  Trash2, Edit2, X, Save, Phone, Mail, Copy,
  GanttChart, Kanban, Settings, Paperclip, Upload, Download, File, FileText, Image,
  MessageSquare, Users, UserPlus,
} from "lucide-react";

const N8N_TAREA_COMPLETADA = "https://tuaplicacion.app.n8n.cloud/webhook/tarea-completada";
const APP_URL              = "https://app.tuaplicacion.cl";
const BUCKET               = "archivos-tareas";

const C = {
  bg:"#f8fafc", white:"#ffffff", border:"#e2e8f0",
  accent:"#29abe2", accentL:"#e8f5fc",
  text:"#1e293b", sub:"#64748b", light:"#94a3b8",
  ok:"#10b981", okL:"#d1fae5",
  err:"#ef4444", errL:"#fee2e2",
  warn:"#f59e0b", warnL:"#fef3c7",
};

const iStyle = {
  width:"100%", padding:"8px 12px", borderRadius:7,
  border:`1px solid ${C.border}`, background:C.white,
  color:C.text, fontSize:14, outline:"none",
  transition:"border-color .15s, box-shadow .15s",
  fontFamily:"'DM Sans',sans-serif",
};

const COLS = [
  {key:"Por hacer",   color:"#94a3b8", bg:"#f1f5f9"},
  {key:"En progreso", color:"#f59e0b", bg:"#fef3c7"},
  {key:"En revisión", color:"#818cf8", bg:"#eef2ff"},
  {key:"Completado",  color:"#10b981", bg:"#d1fae5"},
];

const TIPOS = ["Feature","Fix","Integración","Diseño","Consultoría","Infraestructura"];

const TIPO_COLOR = {
  Feature:         {color:"#29abe2", bg:"#e8f5fc"},
  Fix:             {color:"#ef4444", bg:"#fee2e2"},
  Integración:     {color:"#f59e0b", bg:"#fef3c7"},
  Diseño:          {color:"#818cf8", bg:"#eef2ff"},
  Consultoría:     {color:"#10b981", bg:"#d1fae5"},
  Infraestructura: {color:"#64748b", bg:"#f1f5f9"},
};

const EST_PROYECTO  = {
  Activo:     {color:"#29abe2", bg:"#e8f5fc"},
  "En pausa": {color:"#f59e0b", bg:"#fef3c7"},
  Completado: {color:"#10b981", bg:"#d1fae5"},
  Cancelado:  {color:"#ef4444", bg:"#fee2e2"},
};

const ESTADOS_PROYECTO = ["Activo","En pausa","Completado","Cancelado"];

const fmtDate = (d) => d
  ? new Date(d+"T12:00:00").toLocaleDateString("es-CL",{day:"2-digit",month:"short",year:"numeric"})
  : "—";

const diasRestantes = (d) => {
  if (!d) return null;
  return Math.ceil((new Date(d+"T12:00:00") - new Date()) / 864e5);
};

const diffDias = (a,b) => {
  if (!a||!b) return 0;
  return Math.max(0, Math.ceil((new Date(b+"T12:00:00") - new Date(a+"T12:00:00")) / 864e5));
};

const avatarColor = (name) => {
  const colors = ["#29abe2","#10b981","#f59e0b","#818cf8","#ef4444","#06b6d4","#84cc16"];
  let h = 0;
  for (let i=0;i<name.length;i++) h = name.charCodeAt(i) + ((h<<5)-h);
  return colors[Math.abs(h) % colors.length];
};

const Avatar = ({name, size=20}) => {
  if (!name?.trim()) return null;
  const initials = name.trim().split(" ").map(p=>p[0]).slice(0,2).join("").toUpperCase();
  const color    = avatarColor(name);
  return (
    <div title={name} style={{width:size, height:size, borderRadius:"50%",
      background:`${color}20`, border:`1.5px solid ${color}50`,
      display:"flex", alignItems:"center", justifyContent:"center",
      fontSize:size*0.38, fontWeight:700, color, flexShrink:0}}>
      {initials}
    </div>
  );
};

// ── Ícono de archivo según extensión
function ArchivoIcon({ nombre, size=13 }) {
  const ext = (nombre||"").split(".").pop().toLowerCase();
  if (["jpg","jpeg","png","gif","webp","svg"].includes(ext)) return <Image size={size} color={C.accent}/>;
  if (ext==="pdf") return <FileText size={size} color={C.err}/>;
  return <File size={size} color={C.sub}/>;
}

function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes/1024).toFixed(1)} KB`;
  return `${(bytes/1048576).toFixed(1)} MB`;
}

function nombreLegible(name) {
  return name.replace(/^\d{13}-/, "");
}

// ══════════════════════════════════════════
// ── Componente TareaArchivos
// ══════════════════════════════════════════
function TareaArchivos({ tareaId, subtareaId = null }) {
  const [archivos,  setArchivos]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [subiendo,  setSubiendo]  = useState(false);
  const [error,     setError]     = useState("");
  const [dragOver,  setDragOver]  = useState(false);
  const inputRef = useRef();

  const carpeta = subtareaId
    ? `tarea-${tareaId}/sub-${subtareaId}`
    : `tarea-${tareaId}`;

  useEffect(() => { cargar(); }, [tareaId, subtareaId]);

  async function cargar() {
    setLoading(true);
    setError("");
    try {
      const { data, error: err } = await supabase.storage
        .from(BUCKET)
        .list(carpeta, { sortBy: { column: "created_at", order: "desc" } });
      if (err) throw err;
      setArchivos((data||[]).filter(f => f.name !== ".emptyFolderPlaceholder"));
    } catch { setError("Error al cargar archivos"); }
    setLoading(false);
  }

  async function subir(files) {
    if (!files?.length) return;
    setSubiendo(true); setError("");
    const errores = [];
    for (const file of Array.from(files)) {
      const nombre = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g,"_")}`;
      const { error: err } = await supabase.storage
        .from(BUCKET).upload(`${carpeta}/${nombre}`, file, { upsert: false });
      if (err) errores.push(`❌ ${file.name}: ${err.message}`);
    }
    if (errores.length) setError(errores.join(" · "));
    await cargar();
    setSubiendo(false);
  }

  async function descargar(archivo) {
    const { data, error: err } = await supabase.storage
      .from(BUCKET).createSignedUrl(`${carpeta}/${archivo.name}`, 60);
    if (err) { setError("No se pudo generar enlace"); return; }
    window.open(data.signedUrl, "_blank");
  }

  async function eliminar(archivo) {
  if (!window.confirm(`¿Eliminar "${nombreLegible(archivo.name)}"?`)) return;
    const { error: err } = await supabase.storage
      .from(BUCKET).remove([`${carpeta}/${archivo.name}`]);
    if (err) { setError("Error al eliminar"); return; }
    await cargar();
  }

  return (
    <div>
      {/* Header */}
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8}}>
        <div style={{display:"flex", alignItems:"center", gap:6}}>
          <Paperclip size={12} color={C.sub}/>
          <span style={{fontSize:11, fontWeight:700, color:C.sub,
            textTransform:"uppercase", letterSpacing:.5}}>
            Archivos adjuntos {archivos.length > 0 && `(${archivos.length})`}
          </span>
        </div>
        <button
          onClick={() => inputRef.current?.click()} disabled={subiendo}
          style={{display:"flex", alignItems:"center", gap:5, padding:"4px 10px",
            border:`1px solid ${C.accent}`, borderRadius:6, background:C.accentL,
            color:C.accent, fontSize:11, fontWeight:700, cursor:"pointer",
            fontFamily:"'DM Sans',sans-serif", opacity: subiendo ? .7 : 1}}>
          {subiendo
            ? <><Loader size={11} style={{animation:"spin 1s linear infinite"}}/> Subiendo...</>
            : <><Upload size={11}/> Subir archivo</>}
        </button>
        <input ref={inputRef} type="file" multiple style={{display:"none"}}
          onChange={e => subir(e.target.files)}/>
      </div>

      {/* Zona drag & drop */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); subir(e.dataTransfer.files); }}
        onClick={() => !subiendo && archivos.length === 0 && inputRef.current?.click()}
        style={{
          border:`2px dashed ${dragOver ? C.accent : C.border}`,
          borderRadius:8, background:dragOver ? C.accentL : C.bg,
          transition:"all .15s", overflow:"hidden",
          cursor: archivos.length === 0 ? "pointer" : "default",
        }}>

        {loading && (
          <div style={{padding:"14px", textAlign:"center", color:C.light, fontSize:12}}>
            Cargando...
          </div>
        )}

        {!loading && archivos.length === 0 && (
          <div style={{padding:"20px", textAlign:"center", color:C.light, fontSize:12}}>
            <Upload size={18} style={{display:"block", margin:"0 auto 6px", opacity:.4}}/>
            Arrastra archivos aquí o haz clic en "Subir archivo"
          </div>
        )}

        {!loading && archivos.length > 0 && (
          <div style={{display:"flex", flexDirection:"column", gap:0}}>
            {archivos.map((a, i) => (
              <div key={a.id || a.name}
                style={{display:"flex", alignItems:"center", gap:8,
                  padding:"8px 12px", background: i%2===0 ? C.white : C.bg,
                  borderBottom: i < archivos.length-1 ? `1px solid ${C.border}` : "none"}}>
                <ArchivoIcon nombre={a.name}/>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:12, fontWeight:600, color:C.text,
                    overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                    {nombreLegible(a.name)}
                  </div>
                  {a.metadata?.size && (
                    <div style={{fontSize:10, color:C.light}}>{fmtSize(a.metadata.size)}</div>
                  )}
                </div>
                <button onClick={() => descargar(a)} title="Descargar"
                  style={{background:"none", border:"none", cursor:"pointer",
                    color:C.accent, display:"flex", padding:4, borderRadius:5,
                    transition:"background .1s"}}
                  onMouseEnter={e => e.currentTarget.style.background = C.accentL}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}>
                  <Download size={13}/>
                </button>
                <button onClick={() => eliminar(a)} title="Eliminar"
                  style={{background:"none", border:"none", cursor:"pointer",
                    color:C.light, display:"flex", padding:4, borderRadius:5,
                    transition:"color .1s"}}
                  onMouseEnter={e => e.currentTarget.style.color = C.err}
                  onMouseLeave={e => e.currentTarget.style.color = C.light}>
                  <Trash2 size={13}/>
                </button>
              </div>
            ))}
            {/* Agregar más */}
            <div
              onClick={() => inputRef.current?.click()}
              style={{padding:"7px 12px", textAlign:"center", fontSize:11,
                color:C.light, cursor:"pointer", background:C.bg,
                borderTop:`1px dashed ${C.border}`, transition:"color .12s"}}
              onMouseEnter={e => e.currentTarget.style.color = C.accent}
              onMouseLeave={e => e.currentTarget.style.color = C.light}>
              + Agregar más archivos
            </div>
          </div>
        )}
      </div>

      {error && (
        <div style={{marginTop:6, padding:"6px 10px", background:C.errL,
          border:`1px solid ${C.err}30`, borderRadius:6, fontSize:11, color:C.err}}>
          {error}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════
// ── Componente principal Proyectos
// ══════════════════════════════════════════
export default function Proyectos({ inicial = null }) {
  const [view,         setView]         = useState("lista");
  const [resumenCom,   setResumenCom]   = useState({});   // comentarios por proyecto
  const [hiloInicial,  setHiloInicial]  = useState(HILO_GENERAL);
  const [invitarAbierto, setInvitarAbierto] = useState(false);
  const [proyectos,    setProyectos]    = useState([]);
  const [activeP,      setActiveP]      = useState(null);
  const [tareas,       setTareas]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [toast,        setToast]        = useState(null);
  const [preparando,   setPreparando]   = useState(false);
  const [dragging,     setDragging]     = useState(null);
  const [dragOver,     setDragOver]     = useState(null);
  const [toDelete,     setToDelete]     = useState(null);
  const [editTarea,    setEditTarea]    = useState(null);
  const [editProyecto, setEditProyecto] = useState(null);
  const [showContacto, setShowContacto]= useState(false);
  const [nuevaTarea,   setNuevaTarea]   = useState(null);

  const notify = (msg, type="ok") => {
    setToast({msg,type});
    setTimeout(()=>setToast(null),3500);
  };

  const fetchProyectos = useCallback(async () => {
    setLoading(true);
    const {data,error} = await supabase
      .from("progreso_proyectos").select("*").order("created_at",{ascending:false});
    if(error){notify("Error al cargar proyectos","err");console.error(error);}
    else setProyectos(data||[]);
    setLoading(false);
  },[]);

  useEffect(()=>{fetchProyectos();},[fetchProyectos]);

  // Indicadores de comentarios (no leídos, pendientes, último mensaje).
  // Si falla (p. ej. migración aún no aplicada) solo se omiten los indicadores.
  const fetchResumenCom = useCallback(async () => {
    try { setResumenCom(agruparResumenPorProyecto(await resumenComentarios(null))); }
    catch (e) { console.warn("Resumen de comentarios no disponible:", e.message); }
  },[]);
  useEffect(()=>{fetchResumenCom();},[fetchResumenCom]);
  useEffect(()=>suscribirComentarios(null,{onInsert:()=>fetchResumenCom()}),[fetchResumenCom]);

  // Enlace directo desde notificaciones: /?proyecto=PRY-…&tarea=uuid
  const inicialAbierto = useRef(false);
  useEffect(()=>{
    if(!inicial?.proyecto||inicialAbierto.current||loading) return;
    const p = proyectos.find(x=>x.id===inicial.proyecto);
    inicialAbierto.current = true;
    if(!p){notify("El proyecto del enlace no existe o no tienes acceso","err");return;}
    (async()=>{
      setActiveP(p); await fetchTareas(p.id);
      setHiloInicial(inicial.tarea||HILO_GENERAL); setView("comentarios");
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[inicial,loading,proyectos]);

  useEffect(()=>{
    const ch = supabase.channel("proyectos_rt")
      .on("postgres_changes",{event:"*",schema:"public",table:"proyectos"},()=>fetchProyectos())
      .on("postgres_changes",{event:"*",schema:"public",table:"tareas"},()=>{
        fetchProyectos(); if(activeP) fetchTareas(activeP.id);
      })
      .on("postgres_changes",{event:"*",schema:"public",table:"subtareas"},()=>{
        if(activeP) fetchTareas(activeP.id);
      })
      .subscribe();
    return ()=>supabase.removeChannel(ch);
  },[activeP,fetchProyectos]);

  const fetchTareas = async (pid) => {
    const {data,error} = await supabase
      .from("tareas").select("*, subtareas(*)")
      .eq("proyecto_id",pid).order("orden");
    if(error){notify("Error al cargar tareas","err");return;}
    setTareas(data||[]);
  };

  const abrirKanban = async (p) => { setActiveP(p); await fetchTareas(p.id); setView("kanban"); };
  const abrirGantt  = async (p) => { setActiveP(p); await fetchTareas(p.id); setView("gantt");  };
  const abrirComentarios = async (p) => {
    setActiveP(p); await fetchTareas(p.id); setHiloInicial(HILO_GENERAL); setView("comentarios");
  };

  const crearTarea = async (form) => {
    const orden = tareas.filter(t=>t.estado===form.estado).length;
    const {error} = await supabase.from("tareas").insert({
      proyecto_id:     activeP.id,
      descripcion:     form.descripcion.trim(),
      tipo:            form.tipo,
      horas_estimadas: form.horas_estimadas,
      asignado_a:      form.asignado_a?.trim()||"",
      estado:          form.estado,
      orden,
    });
    if(error){notify("Error al crear tarea","err");return;}
    notify("Tarea creada");
    setNuevaTarea(null);
    fetchTareas(activeP.id);
    fetchProyectos();
  };

  const moverTarea = async (tareaId, nuevoEstado) => {
    setTareas(ts=>ts.map(t=>t.id===tareaId?{...t,estado:nuevoEstado}:t));
    const {data,error} = await supabase.rpc("mover_tarea",
      {p_tarea_id:tareaId,p_estado:nuevoEstado});
    if(error||(data&&!data.success)){
      notify("Error al mover tarea","err"); fetchTareas(activeP.id); return;
    }
    fetchProyectos();
    if(nuevoEstado==="Completado"){
      try{
        const tarea = tareas.find(t=>t.id===tareaId);
        const {data:pData} = await supabase
          .from("proyectos").select("token,email,cliente,nombre,estado")
          .eq("id",activeP.id).single();
        const {data:fresh} = await supabase
          .from("progreso_proyectos").select("*").eq("id",activeP.id).single();
        if(fresh) setActiveP(fresh);
        await fetch(N8N_TAREA_COMPLETADA,{
          method:"POST",headers:{"Content-Type":"application/json"},
          body:JSON.stringify({
            proyecto_id:    activeP.id,
            proyecto_nombre:pData.nombre,
            proyecto_estado:pData.estado,
            cliente:        pData.cliente,
            email_cliente:  pData.email,
            tarea_desc:     tarea?.descripcion||"",
            link_proyecto:  `${APP_URL}/?ptoken=${pData.token}`,
          }),
        });
      }catch(e){console.warn("n8n no disponible:",e);}
    }
  };

  const toggleSub = async (subId,actual) => {
    const {error} = await supabase.from("subtareas")
      .update({completada:!actual}).eq("id",subId);
    if(error) notify("Error al actualizar subtarea","err");
    else fetchTareas(activeP.id);
  };

  const confirmDeleteP = async () => {
    const {error} = await supabase.from("proyectos").delete().eq("id",toDelete.id);
    if(error) notify("Error al eliminar","err");
    else{notify("Proyecto eliminado");fetchProyectos();setView("lista");}
    setToDelete(null);
  };

  const guardarTarea = async (form) => {
    const {error:te} = await supabase.from("tareas").update({
      descripcion:form.descripcion, tipo:form.tipo,
      horas_estimadas:form.horas_estimadas, horas_reales:form.horas_reales,
      asignado_a:form.asignado_a?.trim()||"",
      notas:form.notas, fecha_inicio:form.fecha_inicio||null,
      fecha_fin:form.fecha_fin||null,
    }).eq("id",form.id);
    if(te){notify("Error al guardar la tarea","err");return;}
    const originales = (tareas.find(t=>t.id===form.id)?.subtareas||[]);
    const idsOrig    = originales.map(s=>s.id);
    const idsAct     = form.subtareas.filter(s=>s.id).map(s=>s.id);
    const eliminadas = idsOrig.filter(id=>!idsAct.includes(id));
    if(eliminadas.length) await supabase.from("subtareas").delete().in("id",eliminadas);
    for(let i=0;i<form.subtareas.length;i++){
      const sub=form.subtareas[i];
      if(!sub.descripcion?.trim()) continue;
      if(sub.id&&idsOrig.includes(sub.id)){
        await supabase.from("subtareas")
          .update({descripcion:sub.descripcion.trim(),orden:i}).eq("id",sub.id);
      } else {
        await supabase.from("subtareas")
          .insert({tarea_id:form.id,descripcion:sub.descripcion.trim(),orden:i});
      }
    }
    notify("Tarea actualizada"); setEditTarea(null);
    fetchTareas(activeP.id); fetchProyectos();
  };

  const guardarProyecto = async (form) => {
    const {error} = await supabase.from("proyectos").update({
      nombre:form.nombre, cliente:form.cliente, email:form.email,
      telefono:form.telefono, rut:form.rut,
      fecha_inicio:form.fecha_inicio||null,
      fecha_entrega:form.fecha_entrega||null,
      estado:form.estado, notas:form.notas,
      // descripcion solo existe tras la migración 20261008150100; el modal la
      // deja undefined si la columna no está disponible.
      ...(form.descripcion!==undefined?{descripcion:form.descripcion}:{}),
    }).eq("id",form.id);
    if(error){notify("Error al guardar proyecto","err");return;}
    notify("Proyecto actualizado"); setEditProyecto(null);
    setActiveP(prev=>({...prev,...form}));
    fetchProyectos();
  };

  const onDragStart=(e,t)=>{setDragging(t);e.dataTransfer.effectAllowed="move";};
  const onDragEnd  =()=>{setDragging(null);setDragOver(null);};
  const onDragOver =(e,k)=>{e.preventDefault();setDragOver(k);};
  const onDrop     =(e,k)=>{
    e.preventDefault();
    if(dragging&&dragging.estado!==k) moverTarea(dragging.id,k);
    setDragging(null);setDragOver(null);
  };

  const onProyectoCreado = async () => {
    setPreparando(false);
    notify("✅ Proyecto creado");
    await fetchProyectos();
  };

  const copiar=(txt)=>{navigator.clipboard?.writeText(txt);notify("Copiado al portapapeles");};

  const KanbanHeader = () => {
    const dias = diasRestantes(activeP.fecha_entrega);
    // Misma regla que progreso_proyectos, calculada sobre las tareas cargadas
    // para que el % no quede desfasado tras mover tareas.
    const pctHeader = progresoTareas(tareas).pct;
    const rc = resumenCom[activeP.id];
    return (
      <div style={{background:C.white,borderBottom:`1px solid ${C.border}`,
        padding:"0 24px",height:52,display:"flex",alignItems:"center",
        justifyContent:"space-between",flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <button onClick={()=>{setView("lista");setActiveP(null);setTareas([]);}}
            style={{display:"flex",alignItems:"center",gap:5,padding:"5px 12px",
              borderRadius:7,border:`1px solid ${C.border}`,background:"transparent",
              color:C.sub,fontSize:12,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}
            onMouseEnter={e=>e.currentTarget.style.background=C.bg}
            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <ChevronLeft size={14}/> Proyectos
          </button>
          <div style={{width:1,height:20,background:C.border}}/>
          <div>
            <span style={{fontWeight:700,fontSize:14,color:C.text}}>{activeP.nombre}</span>
            <span style={{fontSize:12,color:C.sub,marginLeft:8}}>{activeP.cliente}</span>
          </div>
          {activeP.fecha_entrega&&(
            <div style={{display:"flex",alignItems:"center",gap:4,fontSize:12,
              color:dias<7?C.err:C.sub,padding:"3px 9px",borderRadius:6,
              background:dias<7?C.errL:C.bg,
              border:`1px solid ${dias<7?C.err+"30":C.border}`}}>
              <Clock size={11}/>
              {dias<0?"Vencido":dias===0?"Hoy":dias===1?"1 día":`${dias} días`}
            </div>
          )}
        </div>

        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <div style={{display:"flex",alignItems:"center",gap:7}}>
            <div style={{width:90,height:5,background:C.bg,borderRadius:3}}>
              <div style={{height:"100%",borderRadius:3,
                background:pctHeader===100?C.ok:C.accent,
                width:`${pctHeader}%`}}/>
            </div>
            <span style={{fontSize:12,color:C.sub,fontWeight:500}}>
              {pctHeader}%
            </span>
          </div>
          <div style={{width:1,height:20,background:C.border}}/>

          <div style={{display:"flex",background:C.bg,border:`1px solid ${C.border}`,
            borderRadius:7,overflow:"hidden"}}>
            {[{v:"kanban",icon:<Kanban size={13}/>,label:"Kanban"},
              {v:"gantt", icon:<GanttChart size={13}/>,label:"Gantt"},
              {v:"comentarios", icon:<MessageSquare size={13}/>,label:"Comentarios",
                badge:rc?.nuevos||0, pendiente:!!rc?.pendientes},
              {v:"accesos", icon:<Users size={13}/>,label:"Accesos"}].map(btn=>(
              <button key={btn.v} onClick={()=>{if(btn.v==="comentarios")setHiloInicial(HILO_GENERAL);setView(btn.v);}}
                aria-pressed={view===btn.v}
                title={btn.pendiente?"Hay consultas del cliente pendientes de respuesta":undefined}
                style={{display:"flex",alignItems:"center",gap:5,
                  padding:"5px 11px",border:"none",fontSize:12,
                  fontFamily:"'DM Sans',sans-serif",cursor:"pointer",
                  background:view===btn.v?C.white:"transparent",
                  color:view===btn.v?C.accent:C.sub,
                  fontWeight:view===btn.v?600:400,transition:"all .12s"}}>
                {btn.icon}{btn.label}
                {btn.badge>0&&(
                  <span aria-label={`${btn.badge} sin leer`}
                    style={{minWidth:16,height:16,borderRadius:8,background:C.accent,color:"#fff",
                      fontSize:10,fontWeight:700,display:"inline-flex",alignItems:"center",
                      justifyContent:"center",padding:"0 4px"}}>{btn.badge}</span>
                )}
                {btn.pendiente&&!btn.badge&&(
                  <span aria-label="consultas pendientes"
                    style={{width:7,height:7,borderRadius:"50%",background:C.warn}}/>
                )}
              </button>
            ))}
          </div>

          <div style={{position:"relative"}}>
            <button onClick={()=>setShowContacto(!showContacto)}
              style={{display:"flex",alignItems:"center",gap:5,padding:"5px 11px",
                borderRadius:7,border:`1px solid ${showContacto?C.accent:C.border}`,
                background:showContacto?C.accentL:"transparent",
                color:showContacto?C.accent:C.sub,fontSize:12,cursor:"pointer",
                fontFamily:"'DM Sans',sans-serif",transition:"all .12s"}}>
              <User size={13}/>Contacto
            </button>
            {showContacto&&(
              <div style={{position:"absolute",right:0,top:36,zIndex:200,
                background:C.white,border:`1px solid ${C.border}`,
                borderRadius:10,boxShadow:"0 8px 32px #00000016",
                width:260,padding:"14px 16px"}}
                onMouseLeave={()=>setShowContacto(false)}>
                <div style={{fontWeight:700,fontSize:13,color:C.text,marginBottom:12}}>
                  {activeP.cliente}
                </div>
                {[
                  {icon:<Mail size={13}/>, val:activeP.email,    label:"Email"},
                  {icon:<Phone size={13}/>,val:activeP.telefono, label:"Teléfono"},
                  {icon:<User size={13}/>, val:activeP.rut,      label:"RUT"},
                ].filter(f=>f.val).map(f=>(
                  <div key={f.label} style={{display:"flex",alignItems:"center",
                    justifyContent:"space-between",padding:"6px 0",
                    borderBottom:`1px solid ${C.border}`}}>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <span style={{color:C.sub}}>{f.icon}</span>
                      <span style={{fontSize:12,color:C.text}}>{f.val}</span>
                    </div>
                    <button onClick={()=>copiar(f.val)}
                      style={{background:"transparent",border:"none",cursor:"pointer",
                        color:C.light,padding:2,borderRadius:4,display:"flex",
                        alignItems:"center",transition:"color .12s"}}
                      onMouseEnter={e=>e.currentTarget.style.color=C.accent}
                      onMouseLeave={e=>e.currentTarget.style.color=C.light}>
                      <Copy size={12}/>
                    </button>
                  </div>
                ))}
                {!activeP.email&&!activeP.telefono&&!activeP.rut&&(
                  <div style={{fontSize:12,color:C.light,textAlign:"center",padding:"8px 0"}}>
                    Sin datos de contacto
                  </div>
                )}
                <div style={{marginTop:10}}>
                  <div style={{fontSize:10,fontWeight:600,color:C.light,
                    letterSpacing:.5,textTransform:"uppercase",marginBottom:5}}>
                    Link del proyecto
                  </div>
                  <button onClick={()=>copiar(`${APP_URL}/?ptoken=${activeP.token}`)}
                    style={{display:"flex",alignItems:"center",gap:6,width:"100%",
                      padding:"7px 10px",borderRadius:7,border:`1px solid ${C.border}`,
                      background:C.bg,color:C.accent,fontSize:12,cursor:"pointer",
                      fontFamily:"'DM Sans',sans-serif",fontWeight:500}}
                    onMouseEnter={e=>e.currentTarget.style.background=C.accentL}
                    onMouseLeave={e=>e.currentTarget.style.background=C.bg}>
                    <Copy size={12}/>Copiar link cliente
                  </button>
                </div>
              </div>
            )}
          </div>

          <button onClick={()=>{setInvitarAbierto(true);setView("accesos");}}
            title="Invitar cliente al portal"
            style={{display:"flex",alignItems:"center",gap:5,padding:"5px 11px",
              borderRadius:7,border:`1px solid ${C.accent}`,background:C.accentL,
              color:C.accent,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
            <UserPlus size={13}/> Invitar cliente
          </button>

          <button onClick={()=>setEditProyecto({...activeP})}
            style={{display:"flex",alignItems:"center",gap:5,padding:"5px 11px",
              borderRadius:7,border:`1px solid ${C.border}`,background:"transparent",
              color:C.sub,fontSize:12,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}
            onMouseEnter={e=>{e.currentTarget.style.background=C.bg;e.currentTarget.style.color=C.text;}}
            onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.color=C.sub;}}>
            <Settings size={13}/> Editar
          </button>

          <button onClick={()=>setToDelete(activeP)}
            style={{display:"flex",alignItems:"center",gap:4,padding:"5px 10px",
              borderRadius:7,border:`1px solid ${C.border}`,background:"transparent",
              color:C.sub,fontSize:12,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}
            onMouseEnter={e=>{e.currentTarget.style.background=C.errL;e.currentTarget.style.color=C.err;e.currentTarget.style.borderColor=C.err+"40";}}
            onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.color=C.sub;e.currentTarget.style.borderColor=C.border;}}>
            <Trash2 size={12}/> Eliminar
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{fontFamily:"'DM Sans',sans-serif",color:C.text,minHeight:"100%",background:C.bg}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&display=swap');
        *{box-sizing:border-box}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
        .spinning{animation:spin 1s linear infinite}
        .fade-up{animation:fadeUp .2s ease both}
        .card-hover:hover{box-shadow:0 4px 16px #0000001a !important;transform:translateY(-1px)}
        .task-card{transition:box-shadow .12s,transform .12s,opacity .12s}
        .task-card:hover{box-shadow:0 3px 12px #00000012 !important}
        .task-card.dragging{opacity:.5;transform:rotate(1deg)}
        .col-drop{transition:background .12s}
        .coment-admin-grid{display:grid;grid-template-columns:320px minmax(0,1fr);gap:16px;align-items:start}
        @media (max-width:900px){.coment-admin-grid{grid-template-columns:1fr}}
        input:focus,textarea:focus,select:focus{outline:none;border-color:#29abe2!important;box-shadow:0 0 0 3px #29abe215}
      `}</style>

      {toast&&(
        <div style={{position:"fixed",top:20,right:20,zIndex:9999,
          display:"flex",alignItems:"center",gap:8,background:C.white,
          border:`1px solid ${toast.type==="err"?C.err:C.ok}40`,
          borderLeft:`3px solid ${toast.type==="err"?C.err:C.ok}`,
          borderRadius:8,padding:"11px 16px",fontSize:14,
          color:toast.type==="err"?C.err:C.ok,
          boxShadow:"0 4px 16px #00000014",animation:"fadeIn .2s ease"}}>
          {toast.type==="err"?<AlertCircle size={15}/>:<Check size={15}/>}
          {toast.msg}
        </div>
      )}

      {preparando&&(
        <PrepararProyecto cotizacion={null}
          onConfirm={onProyectoCreado} onCancel={()=>setPreparando(false)}/>
      )}

      {nuevaTarea&&(
        <NuevaTareaModal
          estadoInicial={nuevaTarea.estado}
          onSave={crearTarea}
          onCancel={()=>setNuevaTarea(null)}/>
      )}

      {toDelete&&(
        <div style={{position:"fixed",inset:0,zIndex:9998,
          background:"rgba(15,23,42,0.4)",backdropFilter:"blur(2px)",
          display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:C.white,borderRadius:14,padding:"28px 32px",
            width:420,boxShadow:"0 20px 60px #0000002a",border:`1px solid ${C.border}`}}>
            <div style={{width:44,height:44,borderRadius:"50%",background:C.errL,
              display:"flex",alignItems:"center",justifyContent:"center",marginBottom:16}}>
              <Trash2 size={20} color={C.err}/>
            </div>
            <div style={{fontWeight:700,fontSize:16,marginBottom:8}}>¿Eliminar proyecto?</div>
            <div style={{fontSize:13,color:C.sub,lineHeight:1.6,marginBottom:8}}>
              Eliminarás <strong style={{color:C.text}}>{toDelete.nombre}</strong> junto con todas sus tareas y subtareas.
            </div>
            <div style={{fontSize:12,color:C.err,background:C.errL,
              padding:"8px 12px",borderRadius:7,marginBottom:22}}>
              ⚠ Esta acción no se puede deshacer.
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button onClick={()=>setToDelete(null)}
                style={{padding:"8px 20px",borderRadius:8,border:`1px solid ${C.border}`,
                  background:"transparent",color:C.sub,fontSize:14,cursor:"pointer",
                  fontFamily:"'DM Sans',sans-serif"}}
                onMouseEnter={e=>e.currentTarget.style.background=C.bg}
                onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                Cancelar
              </button>
              <button onClick={confirmDeleteP}
                style={{padding:"8px 22px",borderRadius:8,border:"none",
                  background:C.err,color:"#fff",fontWeight:600,fontSize:14,
                  cursor:"pointer",fontFamily:"'DM Sans',sans-serif",
                  boxShadow:`0 4px 12px ${C.err}40`}}>
                Sí, eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {editTarea&&(
        <EditarTareaModal tarea={editTarea}
          onSave={guardarTarea} onCancel={()=>setEditTarea(null)}
          onActividadComentarios={fetchResumenCom}/>
      )}

      {editProyecto&&(
        <EditarProyectoModal proyecto={editProyecto}
          onSave={guardarProyecto} onCancel={()=>setEditProyecto(null)}/>
      )}

      {/* ════ LISTA ════ */}
      {view==="lista"&&(
        <div style={{padding:"28px 32px"}} className="fade-up">
          <div style={{display:"flex",alignItems:"center",
            justifyContent:"space-between",marginBottom:24}}>
            <p style={{color:C.sub,fontSize:13}}>Gestión de proyectos y tareas de desarrollo</p>
            <div style={{display:"flex",gap:10}}>
              <button onClick={fetchProyectos}
                style={{display:"flex",alignItems:"center",gap:5,padding:"7px 13px",
                  borderRadius:7,border:`1px solid ${C.border}`,background:"transparent",
                  color:C.sub,fontSize:13,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}
                onMouseEnter={e=>e.currentTarget.style.background=C.bg}
                onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <RefreshCw size={13} className={loading?"spinning":""}/>
                {loading?"Cargando...":"Actualizar"}
              </button>
              <button onClick={()=>setPreparando(true)}
                style={{display:"flex",alignItems:"center",gap:6,padding:"8px 16px",
                  borderRadius:8,border:"none",background:C.accent,color:"#fff",
                  fontWeight:600,fontSize:13,cursor:"pointer",
                  fontFamily:"'DM Sans',sans-serif",
                  boxShadow:`0 4px 12px ${C.accent}40`,transition:"background .15s"}}
                onMouseEnter={e=>e.currentTarget.style.background="#1a9fd4"}
                onMouseLeave={e=>e.currentTarget.style.background=C.accent}>
                <Plus size={15}/> Nuevo Proyecto
              </button>
            </div>
          </div>

          {proyectos.length>0&&(()=>{
            const activos    = proyectos.filter(p=>p.estado==="Activo").length;
            const completados= proyectos.filter(p=>p.estado==="Completado").length;
            const totalT     = proyectos.reduce((s,p)=>s+(p.total_tareas||0),0);
            const compT      = proyectos.reduce((s,p)=>s+(p.tareas_completadas||0),0);
            return(
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:24}}>
                {[
                  {label:"Proyectos activos", val:activos,    color:C.accent},
                  {label:"Completados",        val:completados,color:C.ok},
                  {label:"Total tareas",       val:totalT,     color:"#818cf8"},
                  {label:"Tareas completadas", val:compT,      color:C.warn},
                ].map((k,i)=>(
                  <div key={i} style={{background:C.white,border:`1px solid ${C.border}`,
                    borderRadius:10,padding:"16px 20px",boxShadow:"0 1px 3px #0000000a"}}>
                    <div style={{fontSize:12,color:C.sub,fontWeight:500,marginBottom:10}}>{k.label}</div>
                    <div style={{fontSize:26,fontWeight:700,color:k.color}}>{k.val}</div>
                  </div>
                ))}
              </div>
            );
          })()}

          {loading&&(
            <div style={{padding:"48px",textAlign:"center",color:C.sub,fontSize:13,
              display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
              <Loader size={16} className="spinning"/> Cargando proyectos...
            </div>
          )}

          {!loading&&proyectos.length===0&&(
            <div style={{background:C.white,border:`1px solid ${C.border}`,
              borderRadius:12,padding:"56px 24px",textAlign:"center",
              boxShadow:"0 1px 3px #0000000a"}}>
              <FolderKanban size={36} color={C.light} style={{margin:"0 auto 14px"}}/>
              <div style={{fontWeight:600,color:C.sub,marginBottom:6,fontSize:15}}>
                Sin proyectos aún
              </div>
              <div style={{fontSize:13,color:C.light,marginBottom:18}}>
                Crea un proyecto y agrega las tareas desde el kanban
              </div>
              <button onClick={()=>setPreparando(true)}
                style={{padding:"9px 22px",borderRadius:8,border:"none",
                  background:C.accent,color:"#fff",fontWeight:600,fontSize:13,
                  cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                + Nuevo Proyecto
              </button>
            </div>
          )}

          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:16}}>
            {proyectos.map(p=>{
              const est  = EST_PROYECTO[p.estado]||EST_PROYECTO.Activo;
              const dias = diasRestantes(p.fecha_entrega);
              const pct  = p.progreso_pct||0;
              return(
                <div key={p.id} className="card-hover task-card"
                  style={{background:C.white,border:`1px solid ${C.border}`,
                    borderRadius:12,padding:"20px",cursor:"pointer",
                    boxShadow:"0 1px 3px #0000000a",transition:"all .15s"}}
                  onClick={()=>abrirKanban(p)}>
                  <div style={{display:"flex",alignItems:"flex-start",
                    justifyContent:"space-between",marginBottom:12}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:15,color:C.text,
                        marginBottom:4,whiteSpace:"nowrap",overflow:"hidden",
                        textOverflow:"ellipsis"}}>
                        {p.nombre}
                      </div>
                      <div style={{fontSize:11,color:C.sub}}>{p.id}</div>
                    </div>
                    <span style={{padding:"3px 10px",borderRadius:20,fontSize:11,
                      fontWeight:600,color:est.color,background:est.bg,
                      flexShrink:0,marginLeft:8}}>
                      {p.estado}
                    </span>
                  </div>
                  <div style={{display:"flex",gap:14,marginBottom:14}}>
                    <div style={{display:"flex",alignItems:"center",gap:5,
                      fontSize:12,color:C.sub}}>
                      <User size={12}/>{p.cliente}
                    </div>
                    {p.fecha_entrega&&(
                      <div style={{display:"flex",alignItems:"center",gap:5,
                        fontSize:12,color:dias!==null&&dias<7?C.err:C.sub}}>
                        <Calendar size={12}/>{fmtDate(p.fecha_entrega)}
                        {dias!==null&&dias<=7&&dias>=0&&(
                          <span style={{color:C.warn,fontWeight:600}}>({dias}d)</span>
                        )}
                        {dias!==null&&dias<0&&(
                          <span style={{color:C.err,fontWeight:600}}>vencido</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",
                      fontSize:11,color:C.sub,marginBottom:5}}>
                      <span>Progreso</span>
                      <span style={{fontWeight:600,color:pct===100?C.ok:C.text}}>{pct}%</span>
                    </div>
                    <div style={{height:5,background:C.bg,borderRadius:3,overflow:"hidden"}}>
                      <div style={{height:"100%",borderRadius:3,
                        background:pct===100?C.ok:C.accent,
                        width:`${pct}%`,transition:"width .3s ease"}}/>
                    </div>
                  </div>
                  {resumenCom[p.id]&&(()=>{
                    const rc=resumenCom[p.id];
                    return(
                      <div onClick={e=>{e.stopPropagation();abrirComentarios(p);}}
                        role="button" tabIndex={0}
                        onKeyDown={e=>{if(e.key==="Enter"){e.stopPropagation();abrirComentarios(p);}}}
                        aria-label={`Comentarios de ${p.nombre}: ${rc.nuevos} sin leer${rc.pendientes?`, ${rc.pendientes} consultas pendientes`:""}`}
                        style={{display:"flex",alignItems:"center",gap:6,marginBottom:10,
                          padding:"6px 9px",borderRadius:7,fontSize:11,color:C.sub,
                          background:rc.pendientes?C.warnL:C.bg,
                          border:`1px solid ${rc.pendientes?C.warn+"40":C.border}`}}>
                        <MessageSquare size={12} color={rc.nuevos?C.accent:C.light} style={{flexShrink:0}}/>
                        <span style={{flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          <strong style={{color:C.text,fontWeight:600}}>{rc.ultimo?.ultimo_autor}:</strong>{" "}
                          {rc.ultimo?.ultimo_texto} · {fmtRelativo(rc.ultimo?.ultimo_at)}
                        </span>
                        {rc.pendientes>0&&(
                          <span style={{fontWeight:700,color:C.warn,flexShrink:0}}>Pendiente</span>
                        )}
                        {rc.nuevos>0&&(
                          <span style={{minWidth:16,height:16,borderRadius:8,background:C.accent,color:"#fff",
                            fontSize:10,fontWeight:700,display:"inline-flex",alignItems:"center",
                            justifyContent:"center",padding:"0 4px",flexShrink:0}}>{rc.nuevos}</span>
                        )}
                      </div>
                    );
                  })()}
                  <div style={{display:"flex",justifyContent:"space-between",
                    alignItems:"center",paddingTop:10,borderTop:`1px solid ${C.border}`}}>
                    <div style={{fontSize:12,color:C.sub}}>
                      {p.tareas_completadas}/{p.total_tareas} tareas
                    </div>
                    <div style={{display:"flex",gap:12}}>
                      <div style={{display:"flex",alignItems:"center",gap:4,
                        fontSize:12,color:C.accent,fontWeight:500}}
                        onClick={e=>{e.stopPropagation();abrirGantt(p);}}>
                        <GanttChart size={12}/>Gantt
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:4,
                        fontSize:12,color:C.accent,fontWeight:500}}>
                        <ExternalLink size={12}/>Kanban
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ════ KANBAN ════ */}
      {view==="kanban"&&activeP&&(
        <div style={{display:"flex",flexDirection:"column",height:"100%",
          minHeight:"calc(100vh - 58px)"}} className="fade-up">
          <KanbanHeader/>
          <div style={{flex:1,overflowX:"auto",overflowY:"hidden",
            padding:"20px 24px",display:"flex",gap:16}}>
            {COLS.map(col=>{
              const colTareas=tareas.filter(t=>t.estado===col.key);
              const isDT=dragOver===col.key;
              return(
                <div key={col.key} className="col-drop"
                  onDragOver={e=>onDragOver(e,col.key)}
                  onDrop={e=>onDrop(e,col.key)}
                  onDragLeave={()=>setDragOver(null)}
                  style={{width:280,minWidth:280,flexShrink:0,
                    display:"flex",flexDirection:"column",
                    background:isDT?`${col.color}15`:C.bg,
                    border:`2px solid ${isDT?col.color:C.border}`,
                    borderRadius:12,overflow:"hidden",transition:"all .15s"}}>
                  <div style={{padding:"11px 14px",background:C.white,
                    borderBottom:`1px solid ${C.border}`,
                    display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <div style={{display:"flex",alignItems:"center",gap:7}}>
                      <span style={{width:8,height:8,borderRadius:"50%",
                        background:col.color,display:"inline-block"}}/>
                      <span style={{fontWeight:600,fontSize:13,color:C.text}}>{col.key}</span>
                      <span style={{padding:"2px 7px",borderRadius:10,fontSize:11,
                        fontWeight:600,color:col.color,background:col.bg}}>
                        {colTareas.length}
                      </span>
                    </div>
                    <button
                      onClick={()=>setNuevaTarea({estado:col.key})}
                      style={{width:24,height:24,borderRadius:6,border:"none",
                        background:"transparent",color:C.light,cursor:"pointer",
                        display:"flex",alignItems:"center",justifyContent:"center",
                        transition:"all .12s"}}
                      onMouseEnter={e=>{e.currentTarget.style.background=col.bg;e.currentTarget.style.color=col.color;}}
                      onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.color=C.light;}}>
                      <Plus size={15}/>
                    </button>
                  </div>
                  <div style={{flex:1,overflowY:"auto",padding:"10px",
                    display:"flex",flexDirection:"column",gap:8,minHeight:160}}>
                    {colTareas.map(tarea=>(
                      <TareaCard key={tarea.id} tarea={tarea}
                        onDragStart={onDragStart} onDragEnd={onDragEnd}
                        isDragging={dragging?.id===tarea.id}
                        onToggleSub={toggleSub} cols={COLS} onMove={moverTarea}
                        comentarios={resumenCom[activeP.id]?.porTarea[tarea.id]}
                        onEdit={()=>setEditTarea({...tarea,
                          subtareas:(tarea.subtareas||[]).map(s=>({...s}))})}/>
                    ))}
                    {colTareas.length===0&&!isDT&&(
                      <div style={{padding:"16px 0",textAlign:"center",
                        color:C.light,fontSize:12,userSelect:"none"}}>
                        Sin tareas
                      </div>
                    )}
                    {isDT&&(
                      <div style={{padding:"14px",borderRadius:8,
                        border:`2px dashed ${col.color}`,textAlign:"center",
                        fontSize:12,color:col.color,fontWeight:500}}>
                        Soltar aquí
                      </div>
                    )}
                    <button onClick={()=>setNuevaTarea({estado:col.key})}
                      style={{display:"flex",alignItems:"center",gap:5,
                        padding:"7px 10px",borderRadius:8,width:"100%",
                        border:`1px dashed ${C.border}`,background:"transparent",
                        color:C.light,fontSize:12,cursor:"pointer",
                        justifyContent:"center",fontFamily:"'DM Sans',sans-serif",
                        transition:"all .12s",marginTop:4}}
                      onMouseEnter={e=>{e.currentTarget.style.borderColor=col.color;e.currentTarget.style.color=col.color;e.currentTarget.style.background=col.bg;}}
                      onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.color=C.light;e.currentTarget.style.background="transparent";}}>
                      <Plus size={12}/> Agregar tarea
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ════ GANTT ════ */}
      {view==="gantt"&&activeP&&(
        <div style={{display:"flex",flexDirection:"column",height:"100%",
          minHeight:"calc(100vh - 58px)"}} className="fade-up">
          <KanbanHeader/>
          <GanttView proyecto={activeP} tareas={tareas}/>
        </div>
      )}

      {/* ════ ACCESOS DE CLIENTES ════ */}
      {view==="accesos"&&activeP&&(
        <div style={{display:"flex",flexDirection:"column",height:"100%",
          minHeight:"calc(100vh - 58px)"}} className="fade-up">
          <KanbanHeader/>
          <AccesosClientes key={activeP.id} proyecto={activeP} proyectos={proyectos}
            abrirInvitar={invitarAbierto} onCerrarInvitar={()=>setInvitarAbierto(false)}/>
        </div>
      )}

      {/* ════ COMENTARIOS ════ */}
      {view==="comentarios"&&activeP&&(
        <div style={{display:"flex",flexDirection:"column",height:"100%",
          minHeight:"calc(100vh - 58px)"}} className="fade-up">
          <KanbanHeader/>
          <ComentariosProyectoAdmin key={`${activeP.id}-${hiloInicial}`}
            proyecto={activeP} tareas={tareas} hiloInicial={hiloInicial}
            onActividad={fetchResumenCom}/>
        </div>
      )}
    </div>
  );
}

// ── Modal crear nueva tarea
function NuevaTareaModal({estadoInicial, onSave, onCancel}){
  const [form, setForm] = useState({
    descripcion:"", tipo:"Feature", horas_estimadas:8,
    asignado_a:"", estado:estadoInicial,
  });
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (!form.descripcion.trim()) return;
    setSaving(true); await onSave(form); setSaving(false);
  };
  const col = COLS.find(c=>c.key===estadoInicial)||COLS[0];
  return(
    <div style={{position:"fixed",inset:0,zIndex:9995,
      background:"rgba(15,23,42,0.45)",backdropFilter:"blur(3px)",
      display:"flex",alignItems:"center",justifyContent:"center",padding:"24px 16px"}}>
      <div style={{background:C.white,borderRadius:14,width:"100%",maxWidth:480,
        boxShadow:"0 24px 80px #00000020",border:`1px solid ${C.border}`,
        animation:"fadeUp .2s ease"}}>
        <div style={{padding:"16px 22px",borderBottom:`1px solid ${C.border}`,
          display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:8,height:8,borderRadius:"50%",background:col.color}}/>
            <div style={{fontWeight:700,fontSize:15,color:C.text}}>Nueva tarea</div>
            <span style={{padding:"2px 8px",borderRadius:20,fontSize:11,
              fontWeight:600,color:col.color,background:col.bg}}>{estadoInicial}</span>
          </div>
          <button onClick={onCancel}
            style={{background:"transparent",border:"none",cursor:"pointer",
              color:C.light,padding:4,borderRadius:6,display:"flex",alignItems:"center"}}
            onMouseEnter={e=>e.currentTarget.style.color=C.text}
            onMouseLeave={e=>e.currentTarget.style.color=C.light}>
            <X size={17}/>
          </button>
        </div>
        <div style={{padding:"18px 22px"}}>
          <div style={{marginBottom:14}}>
            <label style={{display:"block",fontSize:12,fontWeight:600,color:C.sub,marginBottom:5}}>Descripción *</label>
            <textarea value={form.descripcion}
              onChange={e=>setForm(f=>({...f,descripcion:e.target.value}))}
              rows={2} placeholder="¿Qué hay que hacer?"
              style={{...iStyle,resize:"vertical"}} autoFocus/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
            <div>
              <label style={{display:"block",fontSize:12,fontWeight:600,color:C.sub,marginBottom:5}}>Tipo</label>
              <select value={form.tipo} onChange={e=>setForm(f=>({...f,tipo:e.target.value}))} style={iStyle}>
                {TIPOS.map(t=><option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={{display:"block",fontSize:12,fontWeight:600,color:C.sub,marginBottom:5}}>Horas estimadas</label>
              <input type="number" value={form.horas_estimadas}
                onChange={e=>setForm(f=>({...f,horas_estimadas:parseInt(e.target.value)||0}))} style={iStyle}/>
            </div>
          </div>
          <div style={{marginBottom:20}}>
            <label style={{display:"block",fontSize:12,fontWeight:600,color:C.sub,marginBottom:5}}>
              Asignado a <span style={{fontWeight:400,color:C.light}}>(opcional)</span>
            </label>
            <input value={form.asignado_a}
              onChange={e=>setForm(f=>({...f,asignado_a:e.target.value}))}
              placeholder="Nombre del responsable..." style={iStyle}/>
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button onClick={onCancel}
              style={{padding:"8px 20px",borderRadius:8,border:`1px solid ${C.border}`,
                background:"transparent",color:C.sub,fontSize:14,cursor:"pointer",
                fontFamily:"'DM Sans',sans-serif"}}>Cancelar</button>
            <button onClick={handleSave} disabled={saving||!form.descripcion.trim()}
              style={{display:"flex",alignItems:"center",gap:6,padding:"8px 22px",
                borderRadius:8,border:"none",
                background:saving||!form.descripcion.trim()?"#94a3b8":C.accent,
                color:"#fff",fontWeight:600,fontSize:14,
                cursor:saving||!form.descripcion.trim()?"not-allowed":"pointer",
                fontFamily:"'DM Sans',sans-serif"}}>
              {saving?<><Loader size={13} className="spinning"/>Creando...</>
                :<><Plus size={14}/>Crear tarea</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Gantt
function GanttView({proyecto,tareas}){
  const inicio    = proyecto.fecha_inicio||proyecto.created_at?.slice(0,10);
  const fin       = proyecto.fecha_entrega;
  const totalDias = diffDias(inicio,fin)||30;
  let   acumDias  = 0;
  const tareasConFechas = tareas.map(t=>{
    const dur = Math.max(1,Math.ceil((t.horas_estimadas||8)/8));
    const fi  = t.fecha_inicio||(()=>{
      const d=new Date((inicio||new Date().toISOString().slice(0,10))+"T12:00:00");
      d.setDate(d.getDate()+acumDias); return d.toISOString().slice(0,10);
    })();
    const ff = t.fecha_fin||(()=>{
      const d=new Date(fi+"T12:00:00"); d.setDate(d.getDate()+dur); return d.toISOString().slice(0,10);
    })();
    if(!t.fecha_inicio) acumDias+=dur;
    return {...t,_fi:fi,_ff:ff,_dur:diffDias(fi,ff)||1};
  });
  const colEst={"Por hacer":"#94a3b8","En progreso":"#f59e0b","En revisión":"#818cf8","Completado":"#10b981"};
  const meses=[];
  if(inicio&&fin){
    let d=new Date(inicio+"T12:00:00"); const fend=new Date(fin+"T12:00:00");
    while(d<=fend){
      meses.push({label:d.toLocaleDateString("es-CL",{month:"short",year:"2-digit"}),
        offset:Math.max(0,diffDias(inicio,d.toISOString().slice(0,10)))});
      d.setMonth(d.getMonth()+1);
    }
  }
  return(
    <div style={{flex:1,overflow:"auto",padding:"20px 24px"}}>
      <div style={{background:C.white,border:`1px solid ${C.border}`,
        borderRadius:12,overflow:"hidden",boxShadow:"0 1px 3px #0000000a"}}>
        <div style={{padding:"14px 20px",borderBottom:`1px solid ${C.border}`,
          display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{fontWeight:600,fontSize:13}}>Carta Gantt — {proyecto.nombre}</div>
          <div style={{fontSize:12,color:C.sub}}>{fmtDate(inicio)} → {fmtDate(fin)} · {totalDias} días</div>
        </div>
        <div style={{overflowX:"auto"}}>
          <div style={{minWidth:Math.max(800,totalDias*14+240)}}>
            <div style={{display:"flex",borderBottom:`1px solid ${C.border}`,background:C.bg}}>
              <div style={{width:240,flexShrink:0,padding:"8px 16px",fontSize:11,
                fontWeight:600,color:C.sub,borderRight:`1px solid ${C.border}`}}>TAREA</div>
              <div style={{flex:1,position:"relative",height:32}}>
                {meses.map((m,i)=>(
                  <div key={i} style={{position:"absolute",left:m.offset*14,
                    fontSize:10,color:C.sub,fontWeight:600,padding:"10px 0 0 6px",
                    borderLeft:`1px dashed ${C.border}`}}>{m.label}</div>
                ))}
              </div>
            </div>
            {tareasConFechas.map((t,i)=>{
              const off=Math.max(0,diffDias(inicio||t._fi,t._fi));
              const width=Math.max(14,t._dur*14);
              const color=colEst[t.estado]||"#94a3b8";
              const subs=t.subtareas||[];
              const done=subs.filter(s=>s.completada).length;
              const pct=subs.length>0?Math.round(done/subs.length*100):t.estado==="Completado"?100:0;
              return(
                <div key={t.id} style={{display:"flex",borderBottom:`1px solid ${C.border}`,
                  background:i%2===0?C.white:C.bg,minHeight:46}}>
                  <div style={{width:240,flexShrink:0,padding:"9px 16px",
                    borderRight:`1px solid ${C.border}`,
                    display:"flex",flexDirection:"column",justifyContent:"center"}}>
                    <div style={{display:"flex",alignItems:"center",gap:7}}>
                      <div style={{fontSize:12,fontWeight:600,color:C.text,
                        whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",flex:1}}>
                        {t.descripcion}
                      </div>
                      {t.asignado_a&&<Avatar name={t.asignado_a} size={18}/>}
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:5,marginTop:3}}>
                      <span style={{width:6,height:6,borderRadius:"50%",background:color,display:"inline-block",flexShrink:0}}/>
                      <span style={{fontSize:10,color:C.sub}}>{t.estado}</span>
                      {t.horas_estimadas>0&&<span style={{fontSize:10,color:C.light}}>· {t.horas_estimadas}h</span>}
                    </div>
                  </div>
                  <div style={{flex:1,position:"relative",display:"flex",alignItems:"center",padding:"6px 0"}}>
                    <div style={{position:"absolute",left:off*14,width,height:28,borderRadius:6,
                      background:`${color}30`,border:`1px solid ${color}60`,overflow:"hidden",display:"flex",alignItems:"center"}}>
                      <div style={{position:"absolute",left:0,top:0,bottom:0,width:`${pct}%`,background:`${color}60`}}/>
                      <span style={{position:"relative",zIndex:1,fontSize:10,fontWeight:600,color,
                        padding:"0 7px",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                        {t.descripcion}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
            {tareas.length===0&&(
              <div style={{padding:"36px",textAlign:"center",color:C.sub,fontSize:13}}>Sin tareas para mostrar</div>
            )}
          </div>
        </div>
        <div style={{padding:"12px 20px",borderTop:`1px solid ${C.border}`,
          background:C.bg,display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
          {Object.entries(colEst).map(([est,col])=>(
            <div key={est} style={{display:"flex",alignItems:"center",gap:5}}>
              <span style={{width:10,height:10,borderRadius:2,background:col,display:"inline-block"}}/>
              <span style={{fontSize:11,color:C.sub}}>{est}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Tarjeta tarea (con indicador de archivos)
function TareaCard({tarea,onDragStart,onDragEnd,isDragging,onToggleSub,cols,onMove,onEdit,comentarios}){
  const [expandido,   setExpandido]   = useState(false);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [numArchivos, setNumArchivos] = useState(null);
  const tc   = TIPO_COLOR[tarea.tipo]||TIPO_COLOR.Feature;
  const subs = tarea.subtareas||[];
  const done = subs.filter(s=>s.completada).length;
  const pct  = subs.length>0?Math.round(done/subs.length*100):0;

  // Contar archivos en background
  useEffect(() => {
    supabase.storage.from(BUCKET).list(`tarea-${tarea.id}`, { limit: 50 })
      .then(({ data }) => {
        const count = (data||[]).filter(f => f.name !== ".emptyFolderPlaceholder").length;
        setNumArchivos(count);
      }).catch(() => {});
  }, [tarea.id]);

  return(
    <div draggable
      onDragStart={e=>onDragStart(e,tarea)} onDragEnd={onDragEnd}
      className={`task-card${isDragging?" dragging":""}`}
      style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:9,
        padding:"12px 13px",cursor:"grab",userSelect:"none",
        boxShadow:"0 1px 3px #0000000a",opacity:isDragging?.4:1}}>
      <div style={{display:"flex",alignItems:"flex-start",
        justifyContent:"space-between",gap:8,marginBottom:6}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:600,fontSize:13,color:C.text,lineHeight:1.4}}>
            {tarea.descripcion}
          </div>
        </div>
        <div style={{position:"relative",flexShrink:0}}>
          <button onClick={e=>{e.stopPropagation();setMenuAbierto(!menuAbierto);}}
            style={{background:"transparent",border:"none",cursor:"pointer",
              color:C.light,padding:2,borderRadius:4,display:"flex",alignItems:"center"}}
            onMouseEnter={e=>e.currentTarget.style.color=C.sub}
            onMouseLeave={e=>e.currentTarget.style.color=C.light}>
            <MoreHorizontal size={14}/>
          </button>
          {menuAbierto&&(
            <div style={{position:"absolute",right:0,top:22,zIndex:100,
              background:C.white,border:`1px solid ${C.border}`,
              borderRadius:8,boxShadow:"0 8px 24px #00000014",
              minWidth:160,overflow:"hidden"}}
              onMouseLeave={()=>setMenuAbierto(false)}>
              <button onClick={()=>{onEdit();setMenuAbierto(false);}}
                style={{width:"100%",display:"flex",alignItems:"center",gap:8,
                  padding:"8px 14px",border:"none",background:"transparent",
                  color:C.text,fontSize:13,cursor:"pointer",
                  fontFamily:"'DM Sans',sans-serif",textAlign:"left",
                  borderBottom:`1px solid ${C.border}`}}
                onMouseEnter={e=>e.currentTarget.style.background=C.accentL}
                onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <Edit2 size={13} color={C.accent}/> Editar tarea
              </button>
              <div style={{padding:"4px 14px",fontSize:11,fontWeight:600,
                color:C.light,letterSpacing:.5,textTransform:"uppercase",marginTop:4}}>
                Mover a
              </div>
              {cols.filter(c=>c.key!==tarea.estado).map(c=>(
                <button key={c.key}
                  onClick={()=>{onMove(tarea.id,c.key);setMenuAbierto(false);}}
                  style={{width:"100%",display:"flex",alignItems:"center",gap:8,
                    padding:"7px 14px",border:"none",background:"transparent",
                    color:C.text,fontSize:13,cursor:"pointer",
                    fontFamily:"'DM Sans',sans-serif",textAlign:"left"}}
                  onMouseEnter={e=>e.currentTarget.style.background=C.bg}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <span style={{width:7,height:7,borderRadius:"50%",background:c.color,flexShrink:0}}/>{c.key}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",
        marginBottom:subs.length>0?8:0}}>
        <span style={{padding:"1px 7px",borderRadius:4,fontSize:10,fontWeight:600,
          color:tc.color,background:tc.bg}}>{tarea.tipo}</span>
        {tarea.horas_estimadas>0&&(
          <span style={{fontSize:11,color:C.light,display:"flex",alignItems:"center",gap:3}}>
            <Clock size={10}/>{tarea.horas_estimadas}h
          </span>
        )}
        {tarea.horas_reales>0&&(
          <span style={{fontSize:11,color:C.sub}}>· {tarea.horas_reales}h reales</span>
        )}
        {/* Indicador archivos */}
        {numArchivos > 0 && (
          <span style={{display:"flex",alignItems:"center",gap:3,fontSize:10,
            color:C.sub,padding:"1px 6px",borderRadius:4,background:C.bg,
            border:`1px solid ${C.border}`}}>
            <Paperclip size={9}/>{numArchivos}
          </span>
        )}
        {/* Indicador comentarios */}
        {comentarios?.total > 0 && (
          <span title={comentarios.pendiente_respuesta?"Consulta del cliente pendiente de respuesta":"Comentarios"}
            aria-label={`${comentarios.total} comentarios, ${comentarios.nuevos} sin leer`}
            style={{display:"flex",alignItems:"center",gap:3,fontSize:10,
              color:comentarios.nuevos?C.accent:C.sub,fontWeight:comentarios.nuevos?700:500,
              padding:"1px 6px",borderRadius:4,
              background:comentarios.pendiente_respuesta?C.warnL:comentarios.nuevos?C.accentL:C.bg,
              border:`1px solid ${comentarios.pendiente_respuesta?C.warn+"60":C.border}`}}>
            <MessageSquare size={9}/>{comentarios.total}
            {comentarios.nuevos>0&&<span>· {comentarios.nuevos} nuevo{comentarios.nuevos>1?"s":""}</span>}
          </span>
        )}
        {tarea.asignado_a&&(
          <div style={{display:"flex",alignItems:"center",gap:4,marginLeft:"auto"}}>
            <Avatar name={tarea.asignado_a} size={18}/>
            <span style={{fontSize:11,color:C.sub,whiteSpace:"nowrap",
              maxWidth:80,overflow:"hidden",textOverflow:"ellipsis"}}>
              {tarea.asignado_a.split(" ")[0]}
            </span>
          </div>
        )}
      </div>

      {subs.length>0&&(
        <div>
          <div style={{display:"flex",alignItems:"center",gap:7,cursor:"pointer",padding:"5px 0"}}
            onClick={()=>setExpandido(!expandido)}>
            <div style={{flex:1,height:3,background:C.bg,borderRadius:2}}>
              <div style={{height:"100%",borderRadius:2,background:C.ok,
                width:`${pct}%`,transition:"width .3s"}}/>
            </div>
            <span style={{fontSize:11,color:C.sub,whiteSpace:"nowrap"}}>{done}/{subs.length}</span>
            {expandido?<ChevronDown size={12} color={C.sub}/>:<ChevronRight size={12} color={C.sub}/>}
          </div>
          {expandido&&(
            <div style={{borderTop:`1px solid ${C.border}`,paddingTop:7,
              display:"flex",flexDirection:"column",gap:4}}>
              {subs.map(sub=>(
                <div key={sub.id}
                  style={{display:"flex",alignItems:"flex-start",gap:7,cursor:"pointer",padding:"2px 0"}}
                  onClick={()=>onToggleSub(sub.id,sub.completada)}>
                  {sub.completada
                    ?<CheckCircle2 size={14} color={C.ok} style={{flexShrink:0,marginTop:1}}/>
                    :<Circle size={14} color={C.light} style={{flexShrink:0,marginTop:1}}/>}
                  <span style={{fontSize:12,color:sub.completada?C.light:C.sub,
                    textDecoration:sub.completada?"line-through":"none",lineHeight:1.4}}>
                    {sub.descripcion}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Modal editar proyecto
function EditarProyectoModal({proyecto,onSave,onCancel}){
  const [form,  setForm]  = useState({...proyecto, descripcion:undefined});
  const [saving,setSaving]= useState(false);
  const [descDisp,setDescDisp]=useState(false);
  // progreso_proyectos no incluye descripcion: se lee desde la tabla.
  useEffect(()=>{
    supabase.from("proyectos").select("descripcion").eq("id",proyecto.id).maybeSingle()
      .then(({data,error})=>{
        if(error||!data) return;
        setDescDisp(true);
        setForm(f=>({...f,descripcion:data.descripcion||""}));
      });
  },[proyecto.id]);
  const handleSave=async()=>{
    if(!form.nombre?.trim()) return;
    setSaving(true); await onSave(form); setSaving(false);
  };
  return(
    <div style={{position:"fixed",inset:0,zIndex:9995,
      background:"rgba(15,23,42,0.45)",backdropFilter:"blur(3px)",
      display:"flex",alignItems:"flex-start",justifyContent:"center",
      padding:"24px 16px",overflowY:"auto"}}>
      <div style={{background:C.white,borderRadius:14,width:"100%",maxWidth:580,
        boxShadow:"0 24px 80px #00000020",border:`1px solid ${C.border}`,
        animation:"fadeUp .2s ease",marginBottom:24}}>
        <div style={{padding:"18px 24px",borderBottom:`1px solid ${C.border}`,
          display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:32,height:32,borderRadius:8,background:C.accentL,
              display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Settings size={15} color={C.accent}/>
            </div>
            <div>
              <div style={{fontWeight:700,fontSize:15,color:C.text}}>Editar proyecto</div>
              <div style={{fontSize:11,color:C.sub,marginTop:1}}>{proyecto.id}</div>
            </div>
          </div>
          <button onClick={onCancel}
            style={{background:"transparent",border:"none",cursor:"pointer",
              color:C.light,padding:4,borderRadius:6,display:"flex",alignItems:"center"}}
            onMouseEnter={e=>e.currentTarget.style.color=C.text}
            onMouseLeave={e=>e.currentTarget.style.color=C.light}>
            <X size={17}/>
          </button>
        </div>
        <div style={{padding:"20px 24px"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:12,marginBottom:14}}>
            <div>
              <label style={{display:"block",fontSize:12,fontWeight:600,color:C.sub,marginBottom:5}}>Nombre *</label>
              <input value={form.nombre||""}
                onChange={e=>setForm(f=>({...f,nombre:e.target.value}))} style={iStyle}/>
            </div>
            <div>
              <label style={{display:"block",fontSize:12,fontWeight:600,color:C.sub,marginBottom:5}}>Estado</label>
              <select value={form.estado||"Activo"}
                onChange={e=>setForm(f=>({...f,estado:e.target.value}))}
                style={{...iStyle,width:"auto"}}>
                {ESTADOS_PROYECTO.map(e=><option key={e}>{e}</option>)}
              </select>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
            <div>
              <label style={{display:"block",fontSize:12,fontWeight:600,color:C.sub,marginBottom:5}}>Inicio</label>
              <input type="date" value={form.fecha_inicio||""}
                onChange={e=>setForm(f=>({...f,fecha_inicio:e.target.value}))} style={iStyle}/>
            </div>
            <div>
              <label style={{display:"block",fontSize:12,fontWeight:600,color:C.sub,marginBottom:5}}>Entrega</label>
              <input type="date" value={form.fecha_entrega||""}
                onChange={e=>setForm(f=>({...f,fecha_entrega:e.target.value}))} style={iStyle}/>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
            {[["cliente","Empresa / Cliente"],["email","Email"],["telefono","Teléfono"],["rut","RUT"]].map(([f,l])=>(
              <div key={f}>
                <label style={{display:"block",fontSize:12,fontWeight:600,color:C.sub,marginBottom:5}}>{l}</label>
                <input value={form[f]||""} onChange={e=>setForm(prev=>({...prev,[f]:e.target.value}))} style={iStyle}/>
              </div>
            ))}
          </div>
          <div style={{marginBottom:20}}>
            {descDisp&&(
              <div style={{marginBottom:14}}>
                <label htmlFor="proy-desc" style={{display:"block",fontSize:12,fontWeight:600,color:C.sub,marginBottom:5}}>
                  Descripción <span style={{fontWeight:400,color:C.light}}>(visible para el cliente en el portal y el enlace público)</span>
                </label>
                <textarea id="proy-desc" value={form.descripcion||""} rows={2}
                  onChange={e=>setForm(f=>({...f,descripcion:e.target.value}))}
                  style={{...iStyle,resize:"vertical"}}/>
              </div>
            )}
            <label style={{display:"block",fontSize:12,fontWeight:600,color:C.sub,marginBottom:5}}>
              Notas internas <span style={{fontWeight:400,color:C.light}}>(no visibles para el cliente)</span>
            </label>
            <textarea value={form.notas||""} rows={2}
              onChange={e=>setForm(f=>({...f,notas:e.target.value}))}
              style={{...iStyle,resize:"vertical"}}/>
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button onClick={onCancel}
              style={{padding:"8px 20px",borderRadius:8,border:`1px solid ${C.border}`,
                background:"transparent",color:C.sub,fontSize:14,cursor:"pointer",
                fontFamily:"'DM Sans',sans-serif"}}>Cancelar</button>
            <button onClick={handleSave} disabled={saving||!form.nombre?.trim()}
              style={{display:"flex",alignItems:"center",gap:6,padding:"8px 22px",
                borderRadius:8,border:"none",
                background:saving||!form.nombre?.trim()?"#94a3b8":C.accent,
                color:"#fff",fontWeight:600,fontSize:14,
                cursor:saving||!form.nombre?.trim()?"not-allowed":"pointer",
                fontFamily:"'DM Sans',sans-serif"}}>
              {saving?<><Loader size={13} className="spinning"/>Guardando...</>:<><Save size={14}/>Guardar</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Modal editar tarea — CON ARCHIVOS
function EditarTareaModal({tarea,onSave,onCancel,onActividadComentarios}){
  const [form,  setForm]  = useState({...tarea});
  const [saving,setSaving]= useState(false);
  const [tabActiva, setTabActiva] = useState("datos"); // "datos" | "archivos" | "comentarios"
  const setSubs=(fn)=>setForm(f=>({...f,subtareas:fn(f.subtareas)}));
  const agregarSub =()=>setSubs(ss=>[...ss,{id:null,descripcion:"",completada:false}]);
  const editarSub  =(idx,val)=>setSubs(ss=>ss.map((s,i)=>i===idx?{...s,descripcion:val}:s));
  const eliminarSub=(idx)=>setSubs(ss=>ss.filter((_,i)=>i!==idx));
  const handleSave=async()=>{
    if(!form.descripcion?.trim()) return;
    setSaving(true); await onSave(form); setSaving(false);
  };
  return(
    <div style={{position:"fixed",inset:0,zIndex:9996,
      background:"rgba(15,23,42,0.45)",backdropFilter:"blur(3px)",
      display:"flex",alignItems:"flex-start",justifyContent:"center",
      padding:"24px 16px",overflowY:"auto"}}>
      <div style={{background:C.white,borderRadius:14,width:"100%",maxWidth:560,
        boxShadow:"0 24px 80px #00000020",border:`1px solid ${C.border}`,
        animation:"fadeUp .2s ease",marginBottom:24}}>

        {/* Header */}
        <div style={{padding:"18px 24px",borderBottom:`1px solid ${C.border}`,
          display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:32,height:32,borderRadius:8,background:C.accentL,
              display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Edit2 size={15} color={C.accent}/>
            </div>
            <div>
              <div style={{fontWeight:700,fontSize:15,color:C.text}}>Editar tarea</div>
              <div style={{fontSize:11,color:C.sub,marginTop:1,
                maxWidth:300,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                {tarea.descripcion}
              </div>
            </div>
          </div>
          <button onClick={onCancel}
            style={{background:"transparent",border:"none",cursor:"pointer",
              color:C.light,padding:4,borderRadius:6,display:"flex",alignItems:"center"}}
            onMouseEnter={e=>e.currentTarget.style.color=C.text}
            onMouseLeave={e=>e.currentTarget.style.color=C.light}>
            <X size={17}/>
          </button>
        </div>

        {/* Tabs */}
        <div style={{display:"flex",borderBottom:`1px solid ${C.border}`,
          background:C.bg,padding:"0 24px"}}>
          {[
            {key:"datos",    label:"Datos y subtareas"},
            {key:"archivos", label:"Archivos adjuntos",  icon:<Paperclip size={12}/>},
            {key:"comentarios", label:"Comentarios",     icon:<MessageSquare size={12}/>},
          ].map(tab=>(
            <button key={tab.key} onClick={()=>setTabActiva(tab.key)}
              style={{display:"flex",alignItems:"center",gap:5,
                padding:"10px 14px",border:"none",background:"transparent",
                color:tabActiva===tab.key?C.accent:C.sub,
                fontWeight:tabActiva===tab.key?700:400,fontSize:13,
                cursor:"pointer",fontFamily:"'DM Sans',sans-serif",
                borderBottom:tabActiva===tab.key?`2px solid ${C.accent}`:"2px solid transparent",
                marginBottom:-1,transition:"all .12s"}}>
              {tab.icon}{tab.label}
            </button>
          ))}
        </div>

        {/* Tab: Datos */}
        {tabActiva==="datos"&&(
          <div style={{padding:"20px 24px"}}>
            <div style={{marginBottom:14}}>
              <label style={{display:"block",fontSize:12,fontWeight:600,color:C.sub,marginBottom:5}}>Descripción *</label>
              <textarea value={form.descripcion||""}
                onChange={e=>setForm(f=>({...f,descripcion:e.target.value}))}
                rows={2} style={{...iStyle,resize:"vertical"}}/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:14}}>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:600,color:C.sub,marginBottom:5}}>Tipo</label>
                <select value={form.tipo||"Feature"}
                  onChange={e=>setForm(f=>({...f,tipo:e.target.value}))} style={iStyle}>
                  {TIPOS.map(t=><option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:600,color:C.sub,marginBottom:5}}>Horas est.</label>
                <input type="number" value={form.horas_estimadas||0}
                  onChange={e=>setForm(f=>({...f,horas_estimadas:parseFloat(e.target.value)||0}))} style={iStyle}/>
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:600,color:C.sub,marginBottom:5}}>Horas reales</label>
                <input type="number" value={form.horas_reales||0}
                  onChange={e=>setForm(f=>({...f,horas_reales:parseFloat(e.target.value)||0}))} style={iStyle}/>
              </div>
            </div>
            <div style={{marginBottom:14}}>
              <label style={{display:"block",fontSize:12,fontWeight:600,color:C.sub,marginBottom:5}}>
                Asignado a <span style={{fontWeight:400,color:C.light}}>(opcional)</span>
              </label>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <Avatar name={form.asignado_a} size={28}/>
                <input value={form.asignado_a||""}
                  onChange={e=>setForm(f=>({...f,asignado_a:e.target.value}))}
                  placeholder="Nombre del responsable..." style={iStyle}/>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:600,color:C.sub,marginBottom:5}}>
                  Fecha inicio <span style={{fontWeight:400,color:C.light}}>(Gantt)</span>
                </label>
                <input type="date" value={form.fecha_inicio||""}
                  onChange={e=>setForm(f=>({...f,fecha_inicio:e.target.value}))} style={iStyle}/>
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:600,color:C.sub,marginBottom:5}}>
                  Fecha fin <span style={{fontWeight:400,color:C.light}}>(Gantt)</span>
                </label>
                <input type="date" value={form.fecha_fin||""}
                  onChange={e=>setForm(f=>({...f,fecha_fin:e.target.value}))} style={iStyle}/>
              </div>
            </div>
            <div style={{marginBottom:18}}>
              <label style={{display:"block",fontSize:12,fontWeight:600,color:C.sub,marginBottom:5}}>Notas</label>
              <textarea value={form.notas||""} rows={2}
                onChange={e=>setForm(f=>({...f,notas:e.target.value}))}
                placeholder="Detalles técnicos, bloqueos..."
                style={{...iStyle,resize:"vertical"}}/>
            </div>

            {/* Subtareas */}
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
              <div style={{flex:1,height:1,background:C.border}}/>
              <span style={{fontSize:11,fontWeight:600,color:C.sub,letterSpacing:.5,textTransform:"uppercase"}}>
                Subtareas ({form.subtareas.length})
              </span>
              <div style={{flex:1,height:1,background:C.border}}/>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:10}}>
              {form.subtareas.map((sub,idx)=>(
                <div key={idx} style={{display:"flex",alignItems:"center",gap:7}}>
                  <span style={{fontSize:11,color:C.light,minWidth:18,textAlign:"right"}}>{idx+1}.</span>
                  <input value={sub.descripcion||""} placeholder="Descripción de la subtarea..."
                    onChange={e=>editarSub(idx,e.target.value)}
                    style={{...iStyle,fontSize:13,padding:"6px 10px"}}/>
                  <button onClick={()=>eliminarSub(idx)}
                    style={{background:"transparent",border:"none",cursor:"pointer",
                      color:C.light,padding:3,borderRadius:5,display:"flex",alignItems:"center",flexShrink:0}}
                    onMouseEnter={e=>e.currentTarget.style.color=C.err}
                    onMouseLeave={e=>e.currentTarget.style.color=C.light}>
                    <Trash2 size={13}/>
                  </button>
                </div>
              ))}
            </div>
            <button onClick={agregarSub}
              style={{display:"flex",alignItems:"center",gap:5,padding:"6px 10px",
                borderRadius:7,marginBottom:20,border:`1px dashed ${C.border}`,
                background:"transparent",color:C.sub,fontSize:12,cursor:"pointer",
                width:"100%",justifyContent:"center",fontFamily:"'DM Sans',sans-serif"}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=C.accent;e.currentTarget.style.color=C.accent;}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.color=C.sub;}}>
              <Plus size={12}/> Agregar subtarea
            </button>

            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button onClick={onCancel}
                style={{padding:"8px 20px",borderRadius:8,border:`1px solid ${C.border}`,
                  background:"transparent",color:C.sub,fontSize:14,cursor:"pointer",
                  fontFamily:"'DM Sans',sans-serif"}}>Cancelar</button>
              <button onClick={handleSave} disabled={saving||!form.descripcion?.trim()}
                style={{display:"flex",alignItems:"center",gap:6,padding:"8px 22px",
                  borderRadius:8,border:"none",
                  background:saving||!form.descripcion?.trim()?"#94a3b8":C.accent,
                  color:"#fff",fontWeight:600,fontSize:14,
                  cursor:saving||!form.descripcion?.trim()?"not-allowed":"pointer",
                  fontFamily:"'DM Sans',sans-serif"}}>
                {saving?<><Loader size={13} className="spinning"/>Guardando...</>:<><Save size={14}/>Guardar cambios</>}
              </button>
            </div>
          </div>
        )}

        {/* Tab: Comentarios */}
        {tabActiva==="comentarios"&&(
          <div style={{padding:"20px 24px"}}>
            <ComentariosHilo proyectoId={tarea.proyecto_id} tareaId={tarea.id} modo="admin"
              titulo="Conversación de esta tarea" alturaMax={340}
              onActividad={onActividadComentarios}/>
            <div style={{marginTop:16,display:"flex",justifyContent:"flex-end"}}>
              <button onClick={onCancel}
                style={{padding:"8px 20px",borderRadius:8,border:`1px solid ${C.border}`,
                  background:"transparent",color:C.sub,fontSize:14,cursor:"pointer",
                  fontFamily:"'DM Sans',sans-serif"}}>Cerrar</button>
            </div>
          </div>
        )}

        {/* Tab: Archivos */}
        {tabActiva==="archivos"&&(
          <div style={{padding:"20px 24px"}}>
            <TareaArchivos tareaId={tarea.id}/>
            <div style={{marginTop:20,display:"flex",justifyContent:"flex-end"}}>
              <button onClick={onCancel}
                style={{padding:"8px 20px",borderRadius:8,border:`1px solid ${C.border}`,
                  background:"transparent",color:C.sub,fontSize:14,cursor:"pointer",
                  fontFamily:"'DM Sans',sans-serif"}}>Cerrar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
